/**
 * Billing preview: tier summary and billing preview (owner/admin only via RPC).
 * Plan: billing_infrastructure_plan §2.1.
 */
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = 'billing-preview';

export interface TierSummary {
  total_systems: number;
  tiers: { monitor: number; alert: number; predict: number };
  tiers_pct?: { monitor: number; alert: number; predict: number };
  highest_tier: string;
  default_tier: string;
  overrides: number;
}

export interface BillingPreview {
  system_count: number;
  volume_discount_pct: number | null;
  tier_subtotals: { monitor: number; alert: number; predict: number };
  monthly_total: number;
  effective_price_per_system: number;
}

const VOLUME_BANDS = [
  { min: 100, discountPct: 15 },
  { min: 250, discountPct: 20 },
  { min: 1000, discountPct: 25 },
] as const;

function getNextVolumeThreshold(total: number): { nextThreshold: number; discountPct: number } | null {
  for (const band of VOLUME_BANDS) {
    if (total < band.min) return { nextThreshold: band.min, discountPct: band.discountPct };
  }
  return null;
}

async function fetchBillingPreview(orgId: string | null): Promise<{
  canSee: boolean;
  tierSummary: TierSummary | null;
  billingPreview: BillingPreview | null;
  nextThreshold: { nextThreshold: number; discountPct: number; systemsToAdd: number } | null;
} | null> {
  if (!orgId) return null;
  const [summaryRes, previewRes] = await Promise.all([
    supabase.rpc('get_org_tier_summary', { p_org_id: orgId }),
    supabase.rpc('get_org_billing_preview', { p_org_id: orgId }),
  ]);
  const tierSummary = (summaryRes.data ?? null) as TierSummary | null;
  const billingPreview = (previewRes.data ?? null) as BillingPreview | null;
  const canSee = tierSummary !== null && billingPreview !== null;
  const total = tierSummary?.total_systems ?? billingPreview?.system_count ?? 0;
  const next = getNextVolumeThreshold(total);
  const nextThreshold = next
    ? { ...next, systemsToAdd: Math.max(0, next.nextThreshold - total) }
    : null;
  return { canSee, tierSummary, billingPreview, nextThreshold };
}

export function useBillingPreview() {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId],
    queryFn: () => fetchBillingPreview(orgId),
    enabled: Boolean(orgId),
  });
  const data = query.data ?? null;
  return {
    canSee: data?.canSee ?? false,
    tierSummary: data?.tierSummary ?? null,
    billingPreview: data?.billingPreview ?? null,
    nextThreshold: data?.nextThreshold ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** True if user can see billing (owner/admin); use for nav visibility. */
export function useCanSeeBilling(): boolean {
  const { userProfile } = useApp();
  const role = userProfile?.permission_role ?? userProfile?.role;
  return role === 'owner' || role === 'admin';
}
